// contexts/DataContext.js
import { createContext, useState, useContext, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

export function DataProvider({ children }) {
  const [menuStructure, setMenuStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentCache, setContentCache] = useState({});
  const [lastUpdatedDate, setLastUpdatedDate] = useState(null);

  useEffect(() => {
    async function fetchMenuStructure() {
      try {
        setLoading(true);
        const collections = await getDocs(collection(db, "kor"));
        const menuPromises = collections.docs.map(async (doc) => {
          const subcollections = await getDocs(collection(db, `kor/${doc.id}/${doc.id}`));

          return {
            id: doc.id,
            title: doc.data().title || doc.id,
            path: `/${doc.id}`,
            order: doc.data().order || 9999,
            subMenus: subcollections.docs.map(subdoc => ({
              id: subdoc.id,
              title: subdoc.data().title || subdoc.id,
              path: `/${doc.id}/${subdoc.id}`,
              order: subdoc.data().order || 9999
            })).sort((a, b) => a.order - b.order)
          };
        });

        const menus = await Promise.all(menuPromises);
        const sortedMenus = menus.sort((a, b) => a.order - b.order);
        setMenuStructure(sortedMenus);
      } catch (err) {
        setError(err);
        console.error("Error fetching menu structure:", err);
      } finally {
        setLoading(false);
      }
    }

    // 마지막 업데이트 날짜 가져오기
    async function fetchLastUpdatedDate() {
      try {
        const dateDocRef = doc(db, 'kor', 'about', 'about', 'site', 'date', 'date');
        const dateDocSnap = await getDoc(dateDocRef);
        
        if (dateDocSnap.exists() && dateDocSnap.data().date) {
          setLastUpdatedDate(dateDocSnap.data().date);
        } else {
          console.log("No date document found or no date field");
        }
      } catch (err) {
        console.error("Error fetching last updated date:", err);
      }
    }
    
    fetchMenuStructure();
    fetchLastUpdatedDate();
  }, []);

  // 콘텐츠 가져오기 함수
  async function fetchContent(category, subcategory) {
    // 캐시 키 생성
    const cacheKey = subcategory ? `${category}/${subcategory}` : category;
    
    // 이미 캐시에 있으면 반환
    if (contentCache[cacheKey]) {
      return contentCache[cacheKey];
    }
    
    try {
      let docRef;
      let mainDocRef;
      let result = {};

      if (subcategory) {
        docRef = doc(db, 'kor', category, category, subcategory);
        mainDocRef = doc(db, 'kor', category);
        
        const [docSnap, mainDocSnap] = await Promise.all([
          getDoc(docRef),
          getDoc(mainDocRef)
        ]);
        
        result = {
          content: docSnap.exists() ? docSnap.data() : null,
          mainContent: mainDocSnap.exists() ? mainDocSnap.data() : null
        };
      } else {
        docRef = doc(db, 'kor', category);
        const docSnap = await getDoc(docRef);
        
        result = {
          content: docSnap.exists() ? docSnap.data() : null
        };

        if (docSnap.exists() && docSnap.data().subcollections) {
          const subCollectionDocs = [];
          
          for (const subCollName of docSnap.data().subcollections) {
            const subDocRef = doc(db, 'kor', category, category, subCollName);
            const subDocSnap = await getDoc(subDocRef);
            
            if (subDocSnap.exists()) {
              subCollectionDocs.push({
                id: subCollName,
                title: subDocSnap.data().title || subCollName,
                ...subDocSnap.data()
              });
            }
          }
          
          result.subCollections = subCollectionDocs;
        }
      }
      
      // 캐시에 저장
      setContentCache(prev => ({
        ...prev,
        [cacheKey]: result
      }));
      
      return result;
    } catch (err) {
      console.error("Error fetching content:", err);
      throw err;
    }
  }

  

  const value = {
    menuStructure,
    loading,
    error,
    fetchContent,
    lastUpdatedDate
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}